import config from '../../config'

import {DateRange, ProviderRatingAggregated} from '../../../../commons/src/types'
import {ProviderId} from '../../types'

import {
  InternalCreditNote,
  InternalCreditNoteV2,
  InternalOrder,
  InternalOrderInvoice,
  InternalOrderInvoiceV2,
} from '../../internal-order-types'
import {AttachmentId, ModelId, OrderId, OrderStatusType, PricingVersion} from '../../order-types'
import httpJsonAuth from '../http-json-auth'

export type DownloadResponse = {
  url: string // S3 signed URL
}

export const getOrders = async (
  providerId: ProviderId,
  creationDateRange: DateRange,
  statusFilters?: OrderStatusType[]
): Promise<InternalOrder[]> => {
  const providerIdQuery = `?providerId=${providerId}`
  const creationDateRangeQuery = `&fromDate=${creationDateRange.from}&toDate=${creationDateRange.to}`
  const statusQuery =
    statusFilters && statusFilters.length > 0 ? `&providerStatus=${statusFilters.join(',')}` : ''
  const response = await httpJsonAuth.fetch(
    `${config.services.internal.baseUrl}/order${providerIdQuery}${creationDateRangeQuery}${statusQuery}`
  )
  return response.json
}

export const getModelDownload = async (
  orderId: OrderId,
  modelId: ModelId
): Promise<DownloadResponse> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.internal.baseUrl}/order/${orderId}/model/${modelId}/download`
  )
  return response.json
}

export const getAttachmentDownload = async (
  orderId: OrderId,
  attachmentId: AttachmentId
): Promise<DownloadResponse> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.internal.baseUrl}/order/${orderId}/attachment/${attachmentId}/download`
  )
  return response.json
}

const createInvoicesAndCreditNotesQuery = (
  providerId: ProviderId,
  creationDateRange: DateRange,
  pricingVersion: PricingVersion
) => {
  const providerIdQuery = `providerId=${providerId}`
  const creationDateRangeQuery = `fromDate=${creationDateRange.from}&toDate=${creationDateRange.to}`
  const includeProviderInvoiceablesQuery = `includeProviderInvoiceables=true`
  const pricingVersionQuery = `pricingVersion=${pricingVersion}`
  return `?${providerIdQuery}&${creationDateRangeQuery}&${includeProviderInvoiceablesQuery}&${pricingVersionQuery}`
}

export const getInvoices = async (
  providerId: ProviderId,
  creationDateRange: DateRange
): Promise<InternalOrderInvoice[]> => {
  const query = createInvoicesAndCreditNotesQuery(providerId, creationDateRange, PricingVersion.V1)
  const response = await httpJsonAuth.fetch(`${config.services.internal.baseUrl}/invoice${query}`)
  return response.json
}

export const getInvoicesV2 = async (
  providerId: ProviderId,
  creationDateRange: DateRange
): Promise<InternalOrderInvoiceV2[]> => {
  const query = createInvoicesAndCreditNotesQuery(providerId, creationDateRange, PricingVersion.V2)
  const response = await httpJsonAuth.fetch(`${config.services.internal.baseUrl}/invoice${query}`)
  return response.json
}

// Note: we use the "refund" route with creditNotesOnly=true to get credit notes
export const getCreditNotes = async (
  providerId: ProviderId,
  creationDateRange: DateRange
): Promise<InternalCreditNote[]> => {
  const query = createInvoicesAndCreditNotesQuery(providerId, creationDateRange, PricingVersion.V1)
  const creditNotesOnlyQuery = `creditNotesOnly=true`
  const response = await httpJsonAuth.fetch(
    `${config.services.internal.baseUrl}/refund${query}&${creditNotesOnlyQuery}`
  )
  return response.json
}

// Note: we use the "refund" route with creditNotesOnly=true to get credit notes
export const getCreditNotesV2 = async (
  providerId: ProviderId,
  creationDateRange: DateRange
): Promise<InternalCreditNoteV2[]> => {
  const query = createInvoicesAndCreditNotesQuery(providerId, creationDateRange, PricingVersion.V2)
  const creditNotesOnlyQuery = `creditNotesOnly=true`
  const response = await httpJsonAuth.fetch(
    `${config.services.internal.baseUrl}/refund${query}&${creditNotesOnlyQuery}`
  )
  return response.json
}

export const getAggregatedReview = async (
  providerId: ProviderId
): Promise<ProviderRatingAggregated> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.internal.baseUrl}/review/aggregate?providerId=${providerId}`
  )
  const result = await response.json
  return result[providerId]
}

export const getModelDownloadAll = async (
  orderId: OrderId,
  providerId: ProviderId
): Promise<Blob> =>
  httpJsonAuth.fetchBlob(
    `${config.services.internal.baseUrl}/order/${orderId}/model/downloadAll?providerId=${providerId}`
  )

export const getModelAttachmentDownload = async (
  orderId: OrderId,
  modelId: ModelId,
  attachmentId: AttachmentId
): Promise<void> => {
  // const response = await httpJsonAuth.fetch(
  //   `${config.services.internal.baseUrl}/order/${orderId}/model/${modelId}/attachment/${attachmentId}/download`
  // )
  // return response.json

  // eslint-disable-next-line no-alert
  alert('Model attachment downloads are currently not implemented in admin mode.')
  return Promise.resolve(undefined)
}
